import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";

/* ───────── What's New banner ─────────
 * Dismissable announcement strip pinned just below the page header on
 * the seller Dashboard. Used to spotlight the latest shipped feature —
 * currently the Myntra Partner API push integration.
 *
 * Dismiss state persists in localStorage under a versioned key so
 * future banners (v=2, v=3, …) re-appear once for sellers who'd
 * already dismissed the previous one.
 */
const BANNER_VERSION = "myntra-push-v1";
const STORAGE_KEY = `bb_whats_new_${BANNER_VERSION}`;

export default function WhatsNewBanner({ ctaHref = "/seller/profile", ctaLabel = "Set up Myntra →" }) {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  function close() {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  }

  if (dismissed) return null;

  return (
    <div
      className="relative mb-6 rounded-2xl overflow-hidden border"
      style={{
        background: "linear-gradient(135deg, #FF3F6C 0%, #BE185D 45%, #7C3AED 100%)",
        borderColor: "rgba(255,255,255,0.15)",
        boxShadow: "0 20px 50px -20px rgba(124,58,237,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
      }}
      data-testid="whats-new-banner"
    >
      {/* Subtle noise / grain overlay for depth */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "22px 22px, 11px 11px",
          backgroundPosition: "0 0, 11px 11px",
        }}
      />
      <div className="relative flex items-center gap-4 px-5 py-4">
        <div
          className="shrink-0 w-10 h-10 rounded-xl grid place-items-center text-white"
          style={{ background: "rgba(255,255,255,0.18)" }}
        >
          <Sparkles size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
              style={{ background: "#FFFFFF", color: "#BE185D" }}
            >
              NEW
            </span>
            <span className="font-bold text-[15px] leading-tight" style={{ color: "#FFFFFF" }}>
              Push catalogs directly to Myntra in 30 seconds
            </span>
          </div>
          <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.88)" }}>
            Photo → AI Listing → Live on Myntra — no more manual portal uploads.
            Connect your Myntra Partner API once and ship every catalog with a single click.
          </div>
        </div>
        <Link
          to={ctaHref}
          className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold shrink-0 transition-transform hover:-translate-y-0.5"
          style={{ background: "#FFFFFF", color: "#BE185D" }}
          data-testid="whats-new-cta"
        >
          {ctaLabel} <ArrowRight size={14} />
        </Link>
        <button
          onClick={close}
          aria-label="Dismiss"
          className="shrink-0 p-1.5 rounded-lg hover:bg-white/15 text-white/70 hover:text-white"
          data-testid="whats-new-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
